import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, ArrowRight, ArrowLeft, Sparkles, Play, Upload, Settings, TrendingUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import confetti from 'canvas-confetti';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: typeof Sparkles;
  route?: string;
  action?: () => Promise<void>;
  completed?: boolean;
}

export function SmartOnboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [userPreferences, setUserPreferences] = useState<any>(null);

  const steps: OnboardingStep[] = [
    {
      id: 'welcome',
      title: 'Welcome to Vewly! 🎉',
      description: "Let's personalize your experience. We'll guide you through the essentials based on what matters most to you.",
      icon: Sparkles,
    },
    {
      id: 'media-upload',
      title: 'Add Your Media',
      description: 'Upload your first movie, show, or music to start building your library.',
      icon: Upload,
      route: '/dashboard',
      action: async () => {
        // Track that user visited upload section
        await trackProgress('media_upload_visited');
      },
    },
    {
      id: 'first-play',
      title: 'Try Playback',
      description: 'Experience seamless streaming with adaptive quality and offline support.',
      icon: Play,
      route: '/media',
      action: async () => {
        await trackProgress('first_playback_initiated');
      },
    },
    {
      id: 'customize',
      title: 'Customize Settings',
      description: 'Set up quality preferences, enable 4K, and configure your perfect viewing experience.',
      icon: Settings,
      route: '/settings',
      action: async () => {
        await trackProgress('settings_configured');
      },
    },
    {
      id: 'explore-features',
      title: 'Discover Pro Features',
      description: 'Explore AI recommendations, IPTV channels, and plugin marketplace.',
      icon: TrendingUp,
      route: '/subscriptions',
      action: async () => {
        await trackProgress('features_explored');
      },
    },
  ];

  useEffect(() => {
    if (!user) return;

    const checkOnboardingStatus = async () => {
      // Check if user has seen smart onboarding
      const { data: onboarding } = await supabase
        .from('user_onboarding')
        .select('*')
        .eq('user_id', user.id)
        .single();

      const hasSeenSmartOnboarding = localStorage.getItem('hasSeenSmartOnboarding');

      if (!hasSeenSmartOnboarding && location.pathname === '/dashboard') {
        // Get user's subscription tier for personalization
        const { data: subscription } = await supabase
          .from('subscriptions')
          .select('tier')
          .eq('user_id', user.id)
          .single();

        setUserPreferences({ tier: subscription?.tier || 'free' });
        setTimeout(() => setIsOpen(true), 1000);
      }

      if (onboarding) {
        const completed = [];
        if (onboarding.guided_tour_completed) completed.push('welcome');
        if (onboarding.first_scan_completed_at) completed.push('media-upload');
        setCompletedSteps(completed);
      }
    };

    checkOnboardingStatus();
  }, [user, location.pathname]);

  const trackProgress = async (action: string) => {
    if (!user) return;

    await supabase.from('user_onboarding').upsert({
      user_id: user.id,
      onboarding_metadata: {
        last_action: action,
        last_action_at: new Date().toISOString(),
      },
    });
  };

  const handleNext = async () => {
    const step = steps[currentStep];

    // Execute step action if exists
    if (step.action) {
      await step.action();
    }

    // Navigate if route specified
    if (step.route && step.route !== location.pathname) {
      navigate(step.route);
    }

    // Mark step as completed
    setCompletedSteps([...completedSteps, step.id]);

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    localStorage.setItem('hasSeenSmartOnboarding', 'true');
    
    await supabase.from('user_onboarding').upsert({
      user_id: user?.id,
      guided_tour_completed: true,
    });

    // Celebrate completion
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
    });

    setIsOpen(false);
  };

  const handleSkip = () => {
    localStorage.setItem('hasSeenSmartOnboarding', 'true');
    setIsOpen(false);
  };

  const progress = ((currentStep + 1) / steps.length) * 100;
  const step = steps[currentStep];
  const StepIcon = step.icon;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center justify-between mb-2">
            <DialogTitle className="text-2xl font-bold">Getting Started</DialogTitle>
            <Badge variant="secondary">
              Step {currentStep + 1} of {steps.length}
            </Badge>
          </div>
          <Progress value={progress} className="h-2" />
        </DialogHeader>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="py-6 space-y-6"
          >
            {/* Step Icon */}
            <div className="flex justify-center">
              <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
                <StepIcon className="h-10 w-10 text-primary" />
              </div>
            </div>

            {/* Step Content */}
            <div className="text-center space-y-3">
              <h3 className="text-2xl font-semibold">{step.title}</h3>
              <p className="text-muted-foreground text-lg">{step.description}</p>
            </div>

            {/* Personalized recommendations based on tier */}
            {currentStep === 0 && userPreferences?.tier === 'free' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-primary/5 rounded-lg p-4 border border-primary/20"
              >
                <p className="text-sm text-center">
                  🎉 <strong>You're on a 7-day free trial!</strong> Enjoy unlimited 4K streaming and explore all features.
                </p>
              </motion.div>
            )}

            {/* Progress indicators */}
            <div className="flex justify-center gap-2 pt-4">
              {steps.map((s, index) => (
                <button
                  key={s.id}
                  onClick={() => setCurrentStep(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentStep
                      ? 'w-8 bg-primary'
                      : completedSteps.includes(s.id)
                      ? 'w-2 bg-primary/50'
                      : 'w-2 bg-muted'
                  }`}
                  aria-label={`Go to step ${index + 1}`}
                />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between items-center pt-4 border-t">
          <Button
            variant="ghost"
            onClick={handleSkip}
            className="text-muted-foreground"
          >
            Skip Tour
          </Button>

          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button variant="outline" onClick={handlePrevious}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Previous
              </Button>
            )}
            <Button onClick={handleNext}>
              {currentStep === steps.length - 1 ? (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Complete
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Export function to restart onboarding
export function restartSmartOnboarding() {
  localStorage.removeItem('hasSeenSmartOnboarding');
  window.location.reload();
}
